// Utility functions for workshop validation

/**
 * Check if a date falls on a weekend (Saturday or Sunday)
 * @param {string|Date} date - The date to check
 * @returns {boolean} - True if the date is a weekend
 */
export const isWeekend = (date) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const dayOfWeek = dateObj.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
};

/**
 * Get the minimum date for workshop selection (tomorrow if today is Friday, otherwise next Monday)
 * @returns {string} - Minimum date in YYYY-MM-DD format
 */
export const getMinWorkshopDate = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  // If today is Friday, minimum date is next Monday
  if (today.getDay() === 5) { // Friday
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + 3); // Friday + 3 = Monday
    return nextMonday.toISOString().split('T')[0];
  }
  
  // Otherwise, minimum date is tomorrow
  return tomorrow.toISOString().split('T')[0];
};

/**
 * Validate workshop date selection
 * @param {string} date - The selected date
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateWorkshopDate = (date) => {
  if (!date) {
    return { isValid: false, message: 'Please select a workshop date' };
  }
  
  if (isWeekend(date)) {
    return { 
      isValid: false, 
      message: 'Workshops cannot be scheduled on weekends. Please select a weekday.' 
    };
  }
  
  const selectedDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (selectedDate < today) {
    return { 
      isValid: false, 
      message: 'Workshop date cannot be in the past. Please select a future date.' 
    };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Get date input props for workshop date selection
 * @returns {object} - Props for date input
 */
export const getWorkshopDateInputProps = () => {
  return {
    min: getMinWorkshopDate(),
    onSelect: (date) => {
      const validation = validateWorkshopDate(date);
      if (!validation.isValid) {
        alert(validation.message);
        return false;
      }
      return true;
    }
  };
};
